import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useQuery } from '@sequencehq/api/utils'
import { useNotifications } from '@sequencehq/core-components'
import { FormFields, toMoney, useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { MINUTES } from '@sequencehq/utils/dates'
import { useCallback, useMemo, useState } from 'react'
import { hubSpotDealLinkErrorMessage } from '../hubSpotDealLinkError'

type FormData = {
  linkToDeal: string
}

type UseLinkHubSpotDealModal = (props: {
  companyId: string
  quoteId: string
  onConfirm: () => void
}) => {
  fields: FormFields<FormData>
  loaded: boolean
  hasDeals: boolean
  isTruncated: boolean
  submit: {
    disabled: boolean
    linking: boolean
    onClick: () => Promise<boolean>
  }
}

export const useLinkHubSpotDealModal: UseLinkHubSpotDealModal = props => {
  const { displayNotification } = useNotifications()
  const [isLinking, setIsLinking] = useState(false)

  const dealsQuery = useQuery(
    dashboard20240730Client.getHubspotCompanyDeals,
    { companyId: props.companyId },
    { staleTime: 1 * MINUTES }
  )

  const deals = useMemo(() => dealsQuery.data?.deals ?? [], [dealsQuery.data])

  const dealOptions = useMemo(
    () =>
      deals.map(deal => ({
        label: deal.name,
        secondaryLabel: [
          deal.amount ? toMoney(deal.amount) : '',
          deal.dealStageLabel ?? ''
        ]
          .filter(Boolean)
          .join(' · '),
        value: deal.dealId
      })),
    [deals]
  )

  const form = useForm<FormData>({
    value: {
      linkToDeal: ''
    },
    fieldConfiguration: [
      {
        property: 'linkToDeal',
        validation: [required],
        options: dealOptions
      }
    ]
  })

  const dealId = form.fields.linkToDeal.value

  const linkDeal = useCallback(async () => {
    setIsLinking(true)

    const res = await dashboard20240730Client.putHubspotDealQuoteLink({
      dealId,
      quoteId: props.quoteId
    })

    setIsLinking(false)

    if (res.error) {
      displayNotification(
        await hubSpotDealLinkErrorMessage(
          res.res,
          'Could not link the quote to HubSpot'
        ),
        { type: 'error' }
      )
      return false
    }

    displayNotification('Quote linked to HubSpot deal', { type: 'success' })
    return true
  }, [dealId, props.quoteId, displayNotification])

  const submit = useMemo(
    () => ({
      disabled: !form.queries.isValid || isLinking,
      linking: isLinking,
      onClick: async (): Promise<boolean> => {
        const success = await linkDeal()

        if (success) {
          props.onConfirm()
        }

        return success
      }
    }),
    [form.queries.isValid, isLinking, linkDeal, props.onConfirm]
  )

  return {
    fields: form.fields,
    loaded: !dealsQuery.isPending,
    hasDeals: deals.length > 0,
    isTruncated: dealsQuery.data?.truncated ?? false,
    submit
  }
}
